import { PAGE_PERMISSION, PERMISSION } from "./catalogue.v2.ts";

export const PERMISSION_ROLES = {
  SysAdmin: "System Admin",
  Doctor: "Doctor",
  Nurse: "Nurse",
  PSO: "PSO",
  Finance: "Finance",
  NurseManager: "Nurse Manager",
  OpsSupport: "Ops Support",
  DoctorAdmin: "Doctor Admin",
  LabUser: "Lab User",
  BO: "BO",
  HelpDesk: "Helpdesk",
  InfectionControlManager: "Infection Control Manager",
  BD: "BD",
  ReadOnly: "Read-only",
  SystemSupport: "System Support",
  ChargeCodeManager: "Charge Code Manager",
  // ECHO-3949: archival-only role; sees Home + ECHO 1.0 Archive only
  TsprgArchivalReadOnly: "TSPRG 1.0 Archival - Read Only",
};

export const ROLES_OBJECT_MAPPING = {
  [PERMISSION_ROLES.SysAdmin]: { label: PERMISSION_ROLES.SysAdmin, value: "815" },
  [PERMISSION_ROLES.Doctor]: { label: PERMISSION_ROLES.Doctor, value: "816" },
  [PERMISSION_ROLES.Nurse]: { label: PERMISSION_ROLES.Nurse, value: "817" },
  [PERMISSION_ROLES.PSO]: { label: PERMISSION_ROLES.PSO, value: "818" },
  [PERMISSION_ROLES.Finance]: { label: PERMISSION_ROLES.Finance, value: "819" },
  [PERMISSION_ROLES.NurseManager]: { label: PERMISSION_ROLES.NurseManager, value: "820" },
  [PERMISSION_ROLES.DoctorAdmin]: { label: PERMISSION_ROLES.DoctorAdmin, value: "821" },
  [PERMISSION_ROLES.OpsSupport]: { label: PERMISSION_ROLES.OpsSupport, value: "822" },
  [PERMISSION_ROLES.LabUser]: { label: PERMISSION_ROLES.LabUser, value: "823" },
  [PERMISSION_ROLES.BO]: { label: PERMISSION_ROLES.BO, value: "824" },
  [PERMISSION_ROLES.HelpDesk]: { label: PERMISSION_ROLES.HelpDesk, value: "825" },
  [PERMISSION_ROLES.InfectionControlManager]: {
    label: PERMISSION_ROLES.InfectionControlManager,
    value: "826",
  },
  [PERMISSION_ROLES.BD]: { label: PERMISSION_ROLES.BD, value: "827" },
  [PERMISSION_ROLES.ReadOnly]: { label: PERMISSION_ROLES.ReadOnly, value: "828" },
  [PERMISSION_ROLES.SystemSupport]: { label: PERMISSION_ROLES.SystemSupport, value: "853" },
  [PERMISSION_ROLES.TsprgArchivalReadOnly]: {
    label: PERMISSION_ROLES.TsprgArchivalReadOnly,
    // Role matching compares labels only; the id differs per environment and is
    // informational here (role created via Role Management).
    value: "tsprg-archival-read-only",
  },
  [PERMISSION_ROLES.ChargeCodeManager]: {
    label: PERMISSION_ROLES.ChargeCodeManager,
    value: "877",
  },
};

// ECHO-3949: every role that existed before the archival-only role. Entries
// pinned to this list keep today's behaviour for current users and are hidden
// from the archival-only role.
const ALL_PRE_EXISTING_ROLES = [
  ROLES_OBJECT_MAPPING[PERMISSION_ROLES.SysAdmin],
  ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Doctor],
  ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Nurse],
  ROLES_OBJECT_MAPPING[PERMISSION_ROLES.PSO],
  ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Finance],
  ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
  ROLES_OBJECT_MAPPING[PERMISSION_ROLES.OpsSupport],
  ROLES_OBJECT_MAPPING[PERMISSION_ROLES.DoctorAdmin],
  ROLES_OBJECT_MAPPING[PERMISSION_ROLES.LabUser],
  ROLES_OBJECT_MAPPING[PERMISSION_ROLES.BO],
  ROLES_OBJECT_MAPPING[PERMISSION_ROLES.HelpDesk],
  ROLES_OBJECT_MAPPING[PERMISSION_ROLES.InfectionControlManager],
  ROLES_OBJECT_MAPPING[PERMISSION_ROLES.BD],
  ROLES_OBJECT_MAPPING[PERMISSION_ROLES.ReadOnly],
  ROLES_OBJECT_MAPPING[PERMISSION_ROLES.SystemSupport],
  ROLES_OBJECT_MAPPING[PERMISSION_ROLES.ChargeCodeManager],
];

export const PREMISSIONS_BY_ROLE_BASE = {
  // ECHO-3950: archive is visible to System Admin and the archival role ONLY
  // (kept out of the System Support auto-grant below on purpose).
  "legacy-archive": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.SysAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.TsprgArchivalReadOnly],
  ],
  // ECHO-3949: reports/invoice group had no role list (visible to everyone);
  // pinned to the pre-existing roles — zero change for current users.
  reports: [...ALL_PRE_EXISTING_ROLES],
  "invoice-nav": [...ALL_PRE_EXISTING_ROLES],
  // The Inventory parent nav was ungated (everyone saw the tab; each child has
  // its own list). Reusing the 3-role "inventory" page list here hid the tab
  // from Doctor/Finance/Nurse/NurseManager/PSO in production — pin to the
  // pre-existing roles like reports/invoice so only the archival role loses it.
  "inventory-nav": [...ALL_PRE_EXISTING_ROLES],
  // patient
  patient: [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.SysAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Doctor],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Nurse],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.PSO],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Finance],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.DoctorAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.OpsSupport],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.BD],
  ],
  "patient-registration-edit": [
    //SysAdmin can not edit patient registration, here is for debugging
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.SysAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Doctor],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Nurse],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.PSO],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.DoctorAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
  ],
  pharmacy: [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.SysAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Doctor],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Nurse],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.PSO],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Finance],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.DoctorAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.OpsSupport],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.LabUser],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.BO],
  ],

  // services

  services: [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.SysAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Doctor],
    // Opens the Services menu so Packages 2.0 is reachable. The other entries under Services keep
    // their own permission groups, which this role is not in, so only Packages 2.0 becomes visible.
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.ChargeCodeManager],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Nurse],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.PSO],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.DoctorAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.OpsSupport],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Finance],
  ],
  "medical-services": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.SysAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Doctor],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Nurse],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.PSO],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.DoctorAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.OpsSupport],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Finance],
  ],
  "medical-services-create": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.SysAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Doctor],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.OpsSupport],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Finance],
  ],
  "medical-services-edit": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.SysAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Doctor],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.OpsSupport],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Finance],
  ],

  package: [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.SysAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Doctor],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Nurse],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.PSO],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.DoctorAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.OpsSupport],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Finance],
  ],
  "package-create": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.SysAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Doctor],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.OpsSupport],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Finance],
  ],
  "package-edit": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.SysAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Doctor],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.OpsSupport],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Finance],
  ],

  // settings

  settings: [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.SysAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Doctor],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Nurse],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.PSO],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Finance],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.DoctorAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.OpsSupport],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.HelpDesk],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.BO],
  ],
  "medical-template": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.SysAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Doctor],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Nurse],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.DoctorAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.OpsSupport],
  ],
  "medical-template-create": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.SysAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Doctor],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Nurse],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.DoctorAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.OpsSupport],
  ],
  "medical-template-edit": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.SysAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Doctor],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Nurse],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.DoctorAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.OpsSupport],
  ],
  "system-preferences": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.SysAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.DoctorAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Doctor],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.OpsSupport],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.BO],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Nurse],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.PSO],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Finance],
  ],
  general: [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.SysAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Doctor],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Nurse],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.PSO],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Finance],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.DoctorAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.OpsSupport],
  ],
  "general-document-category": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.SysAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.DoctorAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.OpsSupport],
  ],
  "general-category-general": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.SysAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Doctor],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Nurse],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.PSO],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Finance],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.DoctorAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.OpsSupport],
  ],
  // "patient-management":[
  //   ROLES_OBJECT_MAPPING[PERMISSION_ROLES.SysAdmin],
  //   ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Doctor],
  //   ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Nurse],
  //   ROLES_OBJECT_MAPPING[PERMISSION_ROLES.PSO],
  //   ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Finance],
  // ],
  "patient-visit": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.SysAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Doctor],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Nurse],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.PSO],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.OpsSupport],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.DoctorAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.BO],
  ],
  "patient-visit-type-management": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.SysAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Doctor],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.OpsSupport],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.BD],
  ],
  "appointments-setup": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.SysAdmin],
    // ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Doctor],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
    // ROLES_OBJECT_MAPPING[PERMISSION_ROLES.OpsSupport],
    // ROLES_OBJECT_MAPPING[PERMISSION_ROLES.DoctorAdmin],
  ],
  "patient-visit-vital-sign": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.SysAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Doctor],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Nurse],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.PSO],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.DoctorAdmin],
  ],
  "patient-visit-backdate-visit": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.SysAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.DoctorAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.OpsSupport],
  ],
  "patient-visit-patient-allergies": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.SysAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.DoctorAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.OpsSupport],
  ],
  inventory: [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.SysAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.OpsSupport],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.DoctorAdmin],
  ],
  "inventory-drug-classification": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.SysAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.OpsSupport],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.DoctorAdmin],
  ],
  // "inventory-frequency-list": [
  //   ROLES_OBJECT_MAPPING[PERMISSION_ROLES.SysAdmin],
  //   ROLES_OBJECT_MAPPING[PERMISSION_ROLES.OpsSupport],
  //   ROLES_OBJECT_MAPPING[PERMISSION_ROLES.DoctorAdmin],
  // ],
  "clinic-services": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.SysAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.OpsSupport],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.DoctorAdmin],
  ],
  "clinic-services-vendor": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.SysAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.OpsSupport],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.DoctorAdmin],
  ],
  "clinic-services-clinic": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.SysAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
  ],
  "clinic-services-clinic-room": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.SysAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
  ],
  billing: [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.SysAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.OpsSupport],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.DoctorAdmin],
  ],
  "billing-payment-method": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.SysAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.OpsSupport],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.DoctorAdmin],
  ],
  "category-setup": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.SysAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.OpsSupport],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.DoctorAdmin],
  ],
  "medispan-clinic-setup": [ROLES_OBJECT_MAPPING[PERMISSION_ROLES.SysAdmin]],
  "user-management": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.SysAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Doctor],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Nurse],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.PSO],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Finance],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.OpsSupport],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.DoctorAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.HelpDesk],
  ],
  "user-management-create": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.SysAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.OpsSupport],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.DoctorAdmin],
  ],
  "user-management-edit": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.SysAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.OpsSupport],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.DoctorAdmin],
  ],
  "role-management": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.SysAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Doctor],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Nurse],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.PSO],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Finance],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.DoctorAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.OpsSupport],
  ],
  "role-management-create": [ROLES_OBJECT_MAPPING[PERMISSION_ROLES.SysAdmin]],
  appointment: [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.SysAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Doctor],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Nurse],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.PSO],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
  ],
  // RAD & LAB
  "rad-lab-management": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.SysAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Doctor],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Nurse],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.PSO],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.OpsSupport],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.DoctorAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.BO],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.BD],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.LabUser],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.HelpDesk],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.InfectionControlManager],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Finance],
  ],
  // inventory
  "inventory-adjustment": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.SysAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Doctor],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Nurse],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.PSO],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Finance],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.DoctorAdmin],
  ],
  "inventory-adjustment-create": [ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Finance]],
  "inventory-adjustment-detail": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.SysAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Nurse],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.PSO],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Finance],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.DoctorAdmin],
  ],
  "erp-consumption-view": [ROLES_OBJECT_MAPPING[PERMISSION_ROLES.SysAdmin]],
  "erp-item-master-view": [ROLES_OBJECT_MAPPING[PERMISSION_ROLES.SysAdmin]],
  "inventory-view": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.SysAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Doctor],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Nurse],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.PSO],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Finance],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.OpsSupport],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.DoctorAdmin],
  ],
  "inventory-create": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.SysAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Nurse],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.PSO],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Finance],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.DoctorAdmin],
  ],
  "inventory-edit": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.SysAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Nurse],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.PSO],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Finance],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.DoctorAdmin],
  ],
  "inventory-delete": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.SysAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Nurse],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.PSO],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Finance],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.DoctorAdmin],
  ],
  "supplier-view": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.SysAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Nurse],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.PSO],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Finance],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.DoctorAdmin],
  ],
  "supplier-create": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.SysAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Nurse],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.PSO],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Finance],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.DoctorAdmin],
  ],
  "supplier-edit": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.SysAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Nurse],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.PSO],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Finance],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.DoctorAdmin],
  ],
  "delivery-order-list": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.SysAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Nurse],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.PSO],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Finance],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.DoctorAdmin],
  ],
  "delivery-order-create": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.SysAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Nurse],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.PSO],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Finance],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.DoctorAdmin],
  ],
  "delivery-order-edit": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.SysAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Nurse],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.PSO],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Finance],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.DoctorAdmin],
  ],
  "dispensing-order-list": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.SysAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Nurse],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.PSO],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Finance],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.DoctorAdmin],
  ],
  "dispensing-order-create": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.SysAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Nurse],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.PSO],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Finance],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.DoctorAdmin],
  ],
  // coverage page
  "coverage-page-update": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Doctor],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Nurse],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Finance],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.DoctorAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.BO],
  ],
  "invoice-view-list": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.SysAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Doctor],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Nurse],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.PSO],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Finance],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.DoctorAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.BO],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.BD],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.LabUser],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.OpsSupport],
  ],
  "credit-note-list": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.OpsSupport],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.DoctorAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.BO],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.BD],
  ],
  "queue-view-list": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.SysAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Doctor],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Finance],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Nurse],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.PSO],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.OpsSupport],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.DoctorAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.LabUser],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.BO],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.HelpDesk],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.InfectionControlManager],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.BD],
  ],

  // ==================== Consultation Module (New Permission System) ====================
  "Consultation.ViewPage": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.SysAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Doctor],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Nurse],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.PSO],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Finance],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.DoctorAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.OpsSupport],
  ],
  "Consultation.EditVitalSign": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.SysAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Doctor],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Nurse],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.PSO],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.DoctorAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
  ],
  "Consultation.DeleteVitalSign": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.SysAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Doctor],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Nurse],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.PSO],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.DoctorAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
  ],
  "Consultation.EditPastMedication": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.SysAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Doctor],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Nurse],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.PSO],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.DoctorAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
  ],
  "Consultation.EditAssessmentPlan": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Doctor],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.DoctorAdmin],
  ],
  "Consultation.DeleteAssessmentPlan": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Doctor],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.DoctorAdmin],
  ],
  "Consultation.SelectConsultationTemplate": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Doctor],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.DoctorAdmin],
  ],
  "Consultation.EditDiagnosis": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Doctor],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.DoctorAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Nurse],
  ],
  "Consultation.EditDispense": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Doctor],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.DoctorAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
  ],
  "Consultation.EditDispenseQuantity": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Doctor],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.DoctorAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Nurse],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.PSO],
  ],
  "Consultation.EditDispenseComment": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Doctor],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.DoctorAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
  ],
  "Consultation.EditDispenseDosage": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Doctor],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.DoctorAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
  ],
  "Consultation.EditItemPrice": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Doctor],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.DoctorAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
  ],
  "Consultation.EditLettersMCs": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Doctor],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.DoctorAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
  ],
  "Consultation.UploadFilesImages": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Doctor],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.DoctorAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
  ],
  "Consultation.AddCharting": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Doctor],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.DoctorAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
  ],
  "Consultation.PrintNote": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Doctor],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.DoctorAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
  ],
  "Consultation.DownloadNotePdf": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Doctor],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.DoctorAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
  ],
  "Consultation.SelectNursingTemplate": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Nurse],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.PSO],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
  ],
  "Consultation.EditNursingNotes": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Nurse],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.PSO],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
  ],
  "Consultation.DeleteNursingNotes": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Nurse],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.PSO],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
  ],
  "Consultation.ViewDiagnosis": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Nurse],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.PSO],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
  ],
  "Consultation.EditDispenseBatchExpire": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Nurse],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.PSO],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
  ],
  "Consultation.EditDispenseItemPrice": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Nurse],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.PSO],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
  ],
  // Letters Print Permission - All specified roles can print regardless of tab, publish status, or lock status
  "Consultation.ViewPrintLettersMCs": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.SysAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Doctor],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.DoctorAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Nurse],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.PSO],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
  ],

  "Consultation.UploadFilesImagesNurse": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Nurse],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.PSO],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
  ],
  "Consultation.ViewCharting": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Nurse],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.PSO],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
  ],
  "Consultation.PrintNoteNurse": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Nurse],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.PSO],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
  ],
  "Consultation.DownloadNotePdfNurse": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Nurse],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.PSO],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
  ],

  // ==================== FormHeader Permissions ====================
  // Consultation Tab FormHeader
  "Consultation.FormHeader.SaveDraft": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Doctor],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.DoctorAdmin],
  ],
  "Consultation.FormHeader.Publish": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Doctor],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.DoctorAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
  ],
  "Consultation.FormHeader.RevertPublish": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.SysAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Doctor],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.DoctorAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
  ],
  "Consultation.FormHeader.CancelVisit": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Doctor],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.DoctorAdmin],
  ],
  "Consultation.FormHeader.TransferPatient": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Doctor],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.DoctorAdmin],
  ],
  "Consultation.FormHeader.BookAppointment": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Doctor],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.DoctorAdmin],
  ],
  "Consultation.FormHeader.CreateTask": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Doctor],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.DoctorAdmin],
  ],
  "Consultation.FormHeader.CreateInternalNote": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Doctor],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.DoctorAdmin],
  ],
  "Consultation.FormHeader.PrintMedicationLabel": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Doctor],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.DoctorAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
  ],

  // Dispensing Tab FormHeader
  "Dispensing.FormHeader.SaveDraft": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Nurse],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.PSO],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
  ],
  "Dispensing.FormHeader.Publish": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Nurse],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.PSO],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
  ],
  "Dispensing.FormHeader.RevertPublish": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.SysAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Nurse],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.PSO],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
  ],
  "Dispensing.FormHeader.CancelVisit": [],
  "Dispensing.FormHeader.TransferPatient": [],
  "Dispensing.FormHeader.BookAppointment": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Nurse],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.PSO],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
  ],
  "Dispensing.FormHeader.CreateTask": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Nurse],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.PSO],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
  ],
  "Dispensing.FormHeader.CreateInternalNote": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Nurse],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.PSO],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
  ],
  "Dispensing.FormHeader.PrintMedicationLabel": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Nurse],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.PSO],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
  ],

  // Dashboard Tab FormHeader
  "Dashboard.FormHeader.SaveDraft": [],
  "Dashboard.FormHeader.Publish": [],
  "Dashboard.FormHeader.RevertPublish": [ROLES_OBJECT_MAPPING[PERMISSION_ROLES.SysAdmin]],
  "Dashboard.FormHeader.CancelVisit": [],
  "Dashboard.FormHeader.TransferPatient": [],
  "Dashboard.FormHeader.BookAppointment": [],
  "Dashboard.FormHeader.CreateTask": [],
  "Dashboard.FormHeader.CreateInternalNote": [],
  "Dashboard.FormHeader.PrintMedicationLabel": [],

  // ==================== Panel Permissions ====================
  // Dispensing Panel
  "Consultation.DispensingPanel.FullEdit": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Doctor],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.DoctorAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
  ],
  "Dispensing.DispensingPanel.FullEdit": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Nurse],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.PSO],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
  ],

  // Letters/MCs Panel - Consultation Tab
  "Consultation.LettersMCsPanel.Create": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Doctor],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.DoctorAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Nurse],
  ],
  "Consultation.LettersMCsPanel.Edit": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Doctor],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.DoctorAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Nurse],
  ],
  "Consultation.LettersMCsPanel.Delete": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Doctor],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.DoctorAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Nurse],
  ],

  // Letters/MCs Panel - Dispensing Tab
  // NurseManager and Nurse can create/edit, Nurse/PSO/NurseManager can delete
  // DoctorAdmin only has print permission, no create/edit/delete
  "Dispensing.LettersMCsPanel.Create": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Nurse],
  ],
  "Dispensing.LettersMCsPanel.Edit": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Nurse],
  ],
  "Dispensing.LettersMCsPanel.Delete": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Nurse],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.PSO],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Nurse],
  ],
  // Letters Print Permission - All specified roles can print regardless of tab, publish status, or lock status
  "Dispensing.LettersMCsPanel.ViewPrint": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.SysAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Doctor],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.DoctorAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Nurse],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.PSO],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.NurseManager],
  ],
  //Charge Code V2
  "charge-code-v2": [
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.SysAdmin],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.Finance],
    ROLES_OBJECT_MAPPING[PERMISSION_ROLES.ChargeCodeManager],
  ],
};

type PermissionRoleEntry = { label: string; value: string };

/** Grant System Support every role-based permission that System Admin already has. */
const withSystemSupportMatchingSysAdmin = <
  T extends Record<string, readonly PermissionRoleEntry[]>,
>(
  permissions: T,
): { [K in keyof T]: PermissionRoleEntry[] } => {
  const sysAdmin = ROLES_OBJECT_MAPPING[PERMISSION_ROLES.SysAdmin];
  const systemSupport = ROLES_OBJECT_MAPPING[PERMISSION_ROLES.SystemSupport];

  return Object.fromEntries(
    Object.entries(permissions).map(([key, roles]) => {
      const hasSysAdmin = roles.some((role) => role.label === sysAdmin.label);
      const hasSystemSupport = roles.some((role) => role.label === systemSupport.label);
      if (hasSysAdmin && !hasSystemSupport) {
        return [key, [...roles, systemSupport]];
      }
      return [key, [...roles]];
    }),
  ) as { [K in keyof T]: PermissionRoleEntry[] };
};

export const PREMISSIONS_BY_ROLE = {
  ...withSystemSupportMatchingSysAdmin(PREMISSIONS_BY_ROLE_BASE),
  // ECHO-3950: strictly System Admin + archival role — no System Support auto-grant
  "legacy-archive": [...PREMISSIONS_BY_ROLE_BASE["legacy-archive"]],
  // System Support may view dispensing orders but cannot create them
  "dispensing-order-create": [...PREMISSIONS_BY_ROLE_BASE["dispensing-order-create"]],
};

/**
 * Legacy (v1) action keys. Kept private: the v1 role tables below are still keyed by these
 * strings, while every call site now passes the v2 name from `catalogue.v2.ts`. The bridge
 * between the two is `V2_TO_LEGACY_ACTION_KEY`.
 */
const ACTIONS = {
  patient: {
    CREATE: "patient.create",
    EDIT: "patient.edit",
    EDIT_ALLERGIES: "patient.edit_allergies",
    ADD_TO_QUEUE: "patient.add_to_queue",
    DEACTIVATE: "patient.deactivate",
    VIEW_LIST: "patient.view_list",
    VIEW_INFO_PREVIEW: "patient.view_info_preview",
    PRINT_LABEL: "patient.print_label",
    PRINT_IN_LIST: "patient.print_in_list",
    VIEW_DASHBOARD: "patient.view_dashboard",
    UPDATE_COVERAGE_PAGE: "patient.update_coverage_page",
    VIEW_DOCUMENT_TAB: "patient.view_document_tab",
    VIEW_INVESTIGATION_TAB: "patient.view_investigation_tab",
    VERIFY_ALLERGIES: "patient.verify_allergies",
    MERGE_PATIENTS: "patient.merge_patients",
  },
  appointment: {
    SELECT_DOCTOR: "appointment.select_doctor",
    VIEW_MY_APPOINTMENTS: "appointment.view_my_appointments",
    EDIT_MY_APPOINTMENTS: "appointment.edit_my_appointments",
    VIEW_ALL_APPOINTMENTS: "appointment.view_all_appointments",
    EDIT_ALL_APPOINTMENTS: "appointment.edit_all_appointments",
    VIEW_ALL_BLOCKED_SLOTS: "appointment.view_all_blocked_slots",
    EDIT_ALL_BLOCKED_SLOTS: "appointment.edit_all_blocked_slots",
    VIEW_MY_BLOCKED_SLOTS: "appointment.view_my_blocked_slots",
    EDIT_MY_BLOCKED_SLOTS: "appointment.edit_my_blocked_slots",
    MONTH_VIEW: "appointment.month_view",
    WEEK_VIEW: "appointment.week_view",
    DAY_VIEW: "appointment.day_view",
    ROOM_VIEW: "appointment.room_view",
    PROVIDER_VIEW: "appointment.provider_view",
  },
  settings: {
    CREATE_EDIT_MEDICAL_TEMPLATE: "settings.create_edit_medical_template",
    ROLE_GROUP_CREATE_ROLE: "settings.role_group_create_role",
    USER_MANAGEMENT_CREATE: "settings.user_management_create",
    USER_MANAGEMENT_EDIT: "settings.user_management_edit",
    USER_MANAGEMENT_RESET_PASSWORD: "settings.user_management_reset_password",
    CLINIC_ROOM_CREATE_EDIT: "settings.clinic_room_create_edit",
    DISCHARGE_ADVICE_CREATE: "settings.discharge_advice_create",
    DISCHARGE_ADVICE_EDIT: "settings.discharge_advice_edit",
    DISCHARGE_ADVICE_DELETE: "settings.discharge_advice_delete",
  },
  services: {
    MEDICAL_SERVICES_CREATE: "services.medical_services_create",
    MEDICAL_SERVICES_EDIT: "services.medical_services_edit",
    PACKAGE_CREATE: "services.package_create",
    PACKAGE_EDIT: "services.package_edit",
  },
  inventory: {
    CREATE_ADJUSTMENT: "inventory.create_adjustment",
    VIEW_ADJUSTMENT: "inventory.view_adjustment",
    CREATE_INVENTORY: "inventory.create_inventory",
    EDIT_INVENTORY: "inventory.edit_inventory",
    DELETE_INVENTORY: "inventory.delete_inventory",
    CREATE_SUPPLIER: "inventory.create_supplier",
    EDIT_SUPPLIER: "inventory.edit_supplier",
    DEACTIVATE_SUPPLIER: "inventory.deactivate_supplier",
    REACTIVATE_SUPPLIER: "inventory.reactivate_supplier",
    CREATE_DELIVERY_ORDER: "inventory.create_delivery_order",
    EDIT_DELIVERY_ORDER: "inventory.edit_delivery_order",
    DELETE_DELIVERY_ORDER: "inventory.delete_delivery_order",
    FINALIZE_EDIT_DELIVERY_ORDER: "inventory.finalize_edit_delivery_order",
    CREATE_DISPENSING_ORDER: "inventory.create_dispensing_order",
    EDIT_DISPENSING_ORDER: "inventory.edit_dispensing_order",
    DELETE_DISPENSING_ORDER: "inventory.delete_dispensing_order",
    FINALIZE_EDIT_DISPENSING_ORDER: "inventory.finalize_edit_dispensing_order",
  },
  invoice: {
    CREATE_CREDIT_NOTE: "invoice.create_credit_note",
    CLOSE_INVOICE: "invoice.close_invoice",
    DELETE_DISPENSE_ITEM: "invoice.delete_dispense_item",
    NOTES_VIEW: "invoice.notes_view",
    NOTES_EDIT: "invoice.notes_edit",
    PRINT_INVOICE: "invoice.print_invoice",
    UNIT_PRICE_EDIT: "invoice.unit_price_edit",
    ITEM_DISCOUNT_EDIT: "invoice.item_discount_edit",
    PROCESS_REFUND: "invoice.process_refund",
    PRINT_CREDIT_NOTE: "invoice.print_credit_note",
    PAYMENT_INVOICE: "invoice.payment_invoice",
    VIEW_INVOICE_LIST: "invoice.view_invoice_list",
    VIEW_CREDIT_NOTE_LIST: "invoice.view_credit_note_list",
    EDIT_PAYMENT: "invoice.edit_payment",
    VOID_PAYMENT: "invoice.void_payment",
  },
  medicalCoverage: {
    VIEW_LIST: "medical_coverage.view_list",
    GENERATE_CORPORATE_INVOICE: "medical_coverage.generate_corporate_invoice",
    RECORD_GENERATE_UPLOAD_CPF_FILE: "medical_coverage.record_generate_upload_cpf_file",
    ADD_DEPOSIT: "medical_coverage.add_deposit",
    PRINT_CORPORATE_INVOICE: "medical_coverage.print_corporate_invoice",
  },
  queue: {
    LINK_APPOINTMENT: "queue.link_appointment",
    PRINT_PATIENT_LABEL: "queue.print_patient_label",
    PRINT_MAILING_LABEL: "queue.print_mailing_label",
    PRINT_MEDICATION_LABEL: "queue.print_medication_label",
    CANCEL_VISIT: "queue.cancel_visit",
    EDIT_NOTES: "queue.edit_notes",
    UPDATE_PROVIDER: "queue.update_provider",
    UPDATE_VISIT_TYPE: "queue.update_visit_type",
    CALL_QUEUE: "queue.call_queue",
  },
  chargeCodeV2: {
    READ_WRITE: "charge_code_v2.read_write",
    SET_ACTIVE: "charge_code_v2.set_active",
  },
};
const SYS_ADMIN_ACTIONS = [
  ACTIONS.patient.EDIT,
  ACTIONS.patient.EDIT_ALLERGIES,
  ACTIONS.patient.ADD_TO_QUEUE,
  ACTIONS.patient.UPDATE_COVERAGE_PAGE,
  ACTIONS.patient.VIEW_DOCUMENT_TAB,
  ACTIONS.patient.VIEW_INVESTIGATION_TAB,
  ACTIONS.appointment.SELECT_DOCTOR,
  ACTIONS.services.MEDICAL_SERVICES_CREATE,
  ACTIONS.services.MEDICAL_SERVICES_EDIT,
  ACTIONS.settings.ROLE_GROUP_CREATE_ROLE,
  ACTIONS.settings.USER_MANAGEMENT_CREATE,
  ACTIONS.settings.USER_MANAGEMENT_EDIT,
  ACTIONS.settings.USER_MANAGEMENT_RESET_PASSWORD,
  ACTIONS.settings.CREATE_EDIT_MEDICAL_TEMPLATE,
  ACTIONS.settings.CLINIC_ROOM_CREATE_EDIT,
  ACTIONS.settings.DISCHARGE_ADVICE_CREATE,
  ACTIONS.settings.DISCHARGE_ADVICE_EDIT,
  ACTIONS.settings.DISCHARGE_ADVICE_DELETE,
  ACTIONS.services.PACKAGE_CREATE,
  ACTIONS.services.PACKAGE_EDIT,
  ACTIONS.inventory.VIEW_ADJUSTMENT,
  ACTIONS.inventory.CREATE_INVENTORY,
  ACTIONS.inventory.EDIT_INVENTORY,
  ACTIONS.inventory.DELETE_INVENTORY,
  ACTIONS.inventory.CREATE_SUPPLIER,
  ACTIONS.inventory.EDIT_SUPPLIER,
  ACTIONS.inventory.DEACTIVATE_SUPPLIER,
  ACTIONS.inventory.REACTIVATE_SUPPLIER,
  ACTIONS.inventory.CREATE_DELIVERY_ORDER,
  ACTIONS.inventory.EDIT_DELIVERY_ORDER,
  ACTIONS.inventory.DELETE_DELIVERY_ORDER,
  ACTIONS.inventory.FINALIZE_EDIT_DELIVERY_ORDER,
  ACTIONS.inventory.CREATE_DISPENSING_ORDER,
  ACTIONS.inventory.EDIT_DISPENSING_ORDER,
  ACTIONS.inventory.DELETE_DISPENSING_ORDER,
  ACTIONS.inventory.FINALIZE_EDIT_DISPENSING_ORDER,
  ACTIONS.invoice.NOTES_VIEW,
  ACTIONS.invoice.PRINT_INVOICE,
  ACTIONS.invoice.PRINT_CREDIT_NOTE,
  ACTIONS.invoice.PAYMENT_INVOICE,
  ACTIONS.invoice.VIEW_INVOICE_LIST,
  ACTIONS.invoice.VIEW_CREDIT_NOTE_LIST,
  ACTIONS.queue.PRINT_PATIENT_LABEL,
  ACTIONS.queue.PRINT_MAILING_LABEL,
  ACTIONS.queue.PRINT_MEDICATION_LABEL,
  ACTIONS.queue.EDIT_NOTES,
  ACTIONS.queue.UPDATE_PROVIDER,
  ACTIONS.queue.UPDATE_VISIT_TYPE,
  ACTIONS.queue.CALL_QUEUE,
  ACTIONS.appointment.VIEW_ALL_APPOINTMENTS,
  ACTIONS.appointment.VIEW_ALL_BLOCKED_SLOTS,
];

export const PREMISSIONS_ACTIONS_BY_ROLE = {
  [PERMISSION_ROLES.SysAdmin]: SYS_ADMIN_ACTIONS,
  // System Support matches System Admin except create/edit dispensing order (view-only)
  [PERMISSION_ROLES.SystemSupport]: SYS_ADMIN_ACTIONS.filter(
    (action) =>
      action !== ACTIONS.inventory.CREATE_DISPENSING_ORDER &&
      action !== ACTIONS.inventory.EDIT_DISPENSING_ORDER &&
      action !== ACTIONS.inventory.FINALIZE_EDIT_DISPENSING_ORDER &&
      action !== ACTIONS.inventory.DELETE_DISPENSING_ORDER,
  ),
  [PERMISSION_ROLES.Doctor]: [
    ACTIONS.patient.EDIT,
    ACTIONS.patient.EDIT_ALLERGIES,
    ACTIONS.patient.CREATE,
    ACTIONS.patient.ADD_TO_QUEUE,
    ACTIONS.patient.DEACTIVATE,
    ACTIONS.patient.UPDATE_COVERAGE_PAGE,
    ACTIONS.patient.VIEW_DOCUMENT_TAB,
    ACTIONS.patient.VIEW_INVESTIGATION_TAB,
    ACTIONS.services.MEDICAL_SERVICES_CREATE,
    ACTIONS.services.MEDICAL_SERVICES_EDIT,
    ACTIONS.settings.CREATE_EDIT_MEDICAL_TEMPLATE,
    ACTIONS.services.PACKAGE_CREATE,
    ACTIONS.services.PACKAGE_EDIT,
    ACTIONS.invoice.CREATE_CREDIT_NOTE,
    ACTIONS.invoice.CLOSE_INVOICE,
    ACTIONS.invoice.DELETE_DISPENSE_ITEM,
    ACTIONS.invoice.NOTES_VIEW,
    ACTIONS.invoice.NOTES_EDIT,
    ACTIONS.invoice.UNIT_PRICE_EDIT,
    ACTIONS.invoice.ITEM_DISCOUNT_EDIT,
    ACTIONS.invoice.VIEW_INVOICE_LIST,
    ACTIONS.invoice.PRINT_INVOICE,
    ACTIONS.medicalCoverage.RECORD_GENERATE_UPLOAD_CPF_FILE,
    ACTIONS.medicalCoverage.ADD_DEPOSIT,
    ACTIONS.medicalCoverage.PRINT_CORPORATE_INVOICE,
    ACTIONS.medicalCoverage.VIEW_LIST,
    ACTIONS.queue.PRINT_PATIENT_LABEL,
    ACTIONS.queue.PRINT_MAILING_LABEL,
    ACTIONS.queue.PRINT_MEDICATION_LABEL,
    ACTIONS.queue.CANCEL_VISIT,
    ACTIONS.queue.EDIT_NOTES,
    ACTIONS.queue.UPDATE_PROVIDER,
    ACTIONS.queue.CALL_QUEUE,
    ACTIONS.patient.VERIFY_ALLERGIES,
    ACTIONS.appointment.VIEW_MY_APPOINTMENTS,
    ACTIONS.appointment.EDIT_MY_APPOINTMENTS,
    ACTIONS.appointment.VIEW_MY_BLOCKED_SLOTS,
    ACTIONS.appointment.EDIT_MY_BLOCKED_SLOTS,
    ACTIONS.appointment.MONTH_VIEW,
    ACTIONS.appointment.WEEK_VIEW,
    ACTIONS.appointment.DAY_VIEW,
    ACTIONS.appointment.ROOM_VIEW,
  ],
  [PERMISSION_ROLES.Nurse]: [
    ACTIONS.patient.CREATE,
    ACTIONS.patient.EDIT,
    ACTIONS.patient.EDIT_ALLERGIES,
    ACTIONS.patient.ADD_TO_QUEUE,
    ACTIONS.patient.DEACTIVATE,
    ACTIONS.patient.UPDATE_COVERAGE_PAGE,
    ACTIONS.patient.VIEW_DOCUMENT_TAB,
    ACTIONS.patient.VIEW_INVESTIGATION_TAB,
    ACTIONS.inventory.VIEW_ADJUSTMENT,
    ACTIONS.inventory.CREATE_INVENTORY,
    ACTIONS.inventory.EDIT_INVENTORY,
    ACTIONS.inventory.DELETE_INVENTORY,
    ACTIONS.inventory.CREATE_SUPPLIER,
    ACTIONS.inventory.EDIT_SUPPLIER,
    ACTIONS.inventory.DEACTIVATE_SUPPLIER,
    ACTIONS.inventory.REACTIVATE_SUPPLIER,
    ACTIONS.inventory.CREATE_DELIVERY_ORDER,
    ACTIONS.inventory.EDIT_DELIVERY_ORDER,
    ACTIONS.inventory.DELETE_DELIVERY_ORDER,
    ACTIONS.inventory.FINALIZE_EDIT_DELIVERY_ORDER,
    ACTIONS.inventory.CREATE_DISPENSING_ORDER,
    ACTIONS.inventory.EDIT_DISPENSING_ORDER,
    ACTIONS.inventory.DELETE_DISPENSING_ORDER,
    ACTIONS.inventory.FINALIZE_EDIT_DISPENSING_ORDER,
    ACTIONS.invoice.CREATE_CREDIT_NOTE,
    ACTIONS.invoice.CLOSE_INVOICE,
    ACTIONS.invoice.DELETE_DISPENSE_ITEM,
    ACTIONS.invoice.NOTES_VIEW,
    ACTIONS.invoice.NOTES_EDIT,
    ACTIONS.invoice.PRINT_INVOICE,
    ACTIONS.invoice.UNIT_PRICE_EDIT,
    ACTIONS.invoice.ITEM_DISCOUNT_EDIT,
    ACTIONS.invoice.PROCESS_REFUND,
    ACTIONS.invoice.PRINT_CREDIT_NOTE,
    ACTIONS.invoice.PAYMENT_INVOICE,
    ACTIONS.invoice.VIEW_INVOICE_LIST,
    ACTIONS.invoice.VIEW_CREDIT_NOTE_LIST,
    ACTIONS.invoice.EDIT_PAYMENT,
    ACTIONS.medicalCoverage.GENERATE_CORPORATE_INVOICE,
    ACTIONS.medicalCoverage.RECORD_GENERATE_UPLOAD_CPF_FILE,
    ACTIONS.medicalCoverage.ADD_DEPOSIT,
    ACTIONS.medicalCoverage.PRINT_CORPORATE_INVOICE,
    ACTIONS.medicalCoverage.VIEW_LIST,
    ACTIONS.queue.PRINT_PATIENT_LABEL,
    ACTIONS.queue.PRINT_MAILING_LABEL,
    ACTIONS.queue.PRINT_MEDICATION_LABEL,
    ACTIONS.queue.CANCEL_VISIT,
    ACTIONS.queue.EDIT_NOTES,
    ACTIONS.queue.UPDATE_PROVIDER,
    ACTIONS.queue.UPDATE_VISIT_TYPE,
    ACTIONS.queue.CALL_QUEUE,
    ACTIONS.appointment.VIEW_ALL_APPOINTMENTS,
    ACTIONS.appointment.EDIT_ALL_APPOINTMENTS,
    ACTIONS.appointment.VIEW_ALL_BLOCKED_SLOTS,
    ACTIONS.appointment.EDIT_ALL_BLOCKED_SLOTS,
    ACTIONS.appointment.PROVIDER_VIEW,
    ACTIONS.appointment.MONTH_VIEW,
    ACTIONS.appointment.WEEK_VIEW,
    ACTIONS.appointment.DAY_VIEW,
    ACTIONS.appointment.ROOM_VIEW,
    ACTIONS.queue.LINK_APPOINTMENT,
  ],
  [PERMISSION_ROLES.PSO]: [
    ACTIONS.patient.CREATE,
    ACTIONS.patient.EDIT,
    ACTIONS.patient.ADD_TO_QUEUE,
    ACTIONS.patient.DEACTIVATE,
    ACTIONS.patient.UPDATE_COVERAGE_PAGE,
    ACTIONS.patient.VIEW_DOCUMENT_TAB,
    ACTIONS.patient.VIEW_INVESTIGATION_TAB,
    ACTIONS.inventory.VIEW_ADJUSTMENT,
    ACTIONS.inventory.CREATE_INVENTORY,
    ACTIONS.inventory.EDIT_INVENTORY,
    ACTIONS.inventory.DELETE_INVENTORY,
    ACTIONS.inventory.CREATE_SUPPLIER,
    ACTIONS.inventory.EDIT_SUPPLIER,
    ACTIONS.inventory.DEACTIVATE_SUPPLIER,
    ACTIONS.inventory.REACTIVATE_SUPPLIER,
    ACTIONS.inventory.CREATE_DELIVERY_ORDER,
    ACTIONS.inventory.EDIT_DELIVERY_ORDER,
    ACTIONS.inventory.DELETE_DELIVERY_ORDER,
    ACTIONS.inventory.FINALIZE_EDIT_DELIVERY_ORDER,
    ACTIONS.inventory.CREATE_DISPENSING_ORDER,
    ACTIONS.inventory.EDIT_DISPENSING_ORDER,
    ACTIONS.inventory.DELETE_DISPENSING_ORDER,
    ACTIONS.inventory.FINALIZE_EDIT_DISPENSING_ORDER,
    ACTIONS.invoice.CREATE_CREDIT_NOTE,
    ACTIONS.invoice.CLOSE_INVOICE,
    ACTIONS.invoice.DELETE_DISPENSE_ITEM,
    ACTIONS.invoice.NOTES_VIEW,
    ACTIONS.invoice.NOTES_EDIT,
    ACTIONS.invoice.PRINT_INVOICE,
    ACTIONS.invoice.UNIT_PRICE_EDIT,
    ACTIONS.invoice.ITEM_DISCOUNT_EDIT,
    ACTIONS.invoice.PROCESS_REFUND,
    ACTIONS.invoice.PRINT_CREDIT_NOTE,
    ACTIONS.invoice.PAYMENT_INVOICE,
    ACTIONS.invoice.VIEW_INVOICE_LIST,
    ACTIONS.invoice.VIEW_CREDIT_NOTE_LIST,
    ACTIONS.invoice.EDIT_PAYMENT,
    ACTIONS.queue.PRINT_PATIENT_LABEL,
    ACTIONS.queue.PRINT_MAILING_LABEL,
    ACTIONS.queue.PRINT_MEDICATION_LABEL,
    ACTIONS.queue.CANCEL_VISIT,
    ACTIONS.queue.EDIT_NOTES,
    ACTIONS.queue.UPDATE_PROVIDER,
    ACTIONS.queue.UPDATE_VISIT_TYPE,
    ACTIONS.queue.CALL_QUEUE,
    ACTIONS.appointment.VIEW_ALL_APPOINTMENTS,
    ACTIONS.appointment.EDIT_ALL_APPOINTMENTS,
    ACTIONS.appointment.VIEW_ALL_BLOCKED_SLOTS,
    ACTIONS.appointment.EDIT_ALL_BLOCKED_SLOTS,
    ACTIONS.appointment.PROVIDER_VIEW,
    ACTIONS.appointment.MONTH_VIEW,
    ACTIONS.appointment.WEEK_VIEW,
    ACTIONS.appointment.DAY_VIEW,
    ACTIONS.appointment.ROOM_VIEW,
    ACTIONS.queue.LINK_APPOINTMENT,
  ],
  [PERMISSION_ROLES.DoctorAdmin]: [
    ACTIONS.patient.CREATE,
    ACTIONS.patient.EDIT,
    ACTIONS.patient.EDIT_ALLERGIES,
    ACTIONS.patient.ADD_TO_QUEUE,
    ACTIONS.patient.DEACTIVATE,
    ACTIONS.patient.UPDATE_COVERAGE_PAGE,
    ACTIONS.patient.VIEW_DOCUMENT_TAB,
    ACTIONS.patient.VIEW_INVESTIGATION_TAB,
    ACTIONS.settings.USER_MANAGEMENT_RESET_PASSWORD,
    ACTIONS.settings.DISCHARGE_ADVICE_CREATE,
    ACTIONS.settings.DISCHARGE_ADVICE_EDIT,
    ACTIONS.settings.DISCHARGE_ADVICE_DELETE,
    ACTIONS.inventory.VIEW_ADJUSTMENT,
    ACTIONS.inventory.CREATE_INVENTORY,
    ACTIONS.inventory.EDIT_INVENTORY,
    ACTIONS.inventory.DELETE_INVENTORY,
    ACTIONS.inventory.CREATE_SUPPLIER,
    ACTIONS.inventory.EDIT_SUPPLIER,
    ACTIONS.inventory.DEACTIVATE_SUPPLIER,
    ACTIONS.inventory.REACTIVATE_SUPPLIER,
    ACTIONS.inventory.CREATE_DELIVERY_ORDER,
    ACTIONS.inventory.EDIT_DELIVERY_ORDER,
    ACTIONS.inventory.DELETE_DELIVERY_ORDER,
    ACTIONS.inventory.FINALIZE_EDIT_DELIVERY_ORDER,
    ACTIONS.inventory.CREATE_DISPENSING_ORDER,
    ACTIONS.inventory.EDIT_DISPENSING_ORDER,
    ACTIONS.inventory.DELETE_DISPENSING_ORDER,
    ACTIONS.inventory.FINALIZE_EDIT_DISPENSING_ORDER,
    ACTIONS.invoice.CREATE_CREDIT_NOTE,
    ACTIONS.invoice.CLOSE_INVOICE,
    ACTIONS.invoice.DELETE_DISPENSE_ITEM,
    ACTIONS.invoice.NOTES_VIEW,
    ACTIONS.invoice.NOTES_EDIT,
    ACTIONS.invoice.PRINT_INVOICE,
    ACTIONS.invoice.UNIT_PRICE_EDIT,
    ACTIONS.invoice.ITEM_DISCOUNT_EDIT,
    ACTIONS.invoice.PRINT_CREDIT_NOTE,
    ACTIONS.invoice.PAYMENT_INVOICE,
    ACTIONS.invoice.VIEW_INVOICE_LIST,
    ACTIONS.invoice.VIEW_CREDIT_NOTE_LIST,
    ACTIONS.queue.PRINT_PATIENT_LABEL,
    ACTIONS.queue.PRINT_MAILING_LABEL,
    ACTIONS.queue.PRINT_MEDICATION_LABEL,
    ACTIONS.queue.CANCEL_VISIT,
    ACTIONS.queue.EDIT_NOTES,
    ACTIONS.queue.UPDATE_PROVIDER,
    ACTIONS.queue.CALL_QUEUE,
    ACTIONS.patient.VERIFY_ALLERGIES,
  ],
  [PERMISSION_ROLES.NurseManager]: [
    ACTIONS.patient.EDIT,
    ACTIONS.patient.EDIT_ALLERGIES,
    ACTIONS.patient.CREATE,
    ACTIONS.patient.ADD_TO_QUEUE,
    ACTIONS.patient.MERGE_PATIENTS,
    ACTIONS.patient.DEACTIVATE,
    ACTIONS.patient.UPDATE_COVERAGE_PAGE,
    ACTIONS.patient.VIEW_DOCUMENT_TAB,
    ACTIONS.patient.VIEW_INVESTIGATION_TAB,
    ACTIONS.services.MEDICAL_SERVICES_CREATE,
    ACTIONS.services.MEDICAL_SERVICES_EDIT,
    ACTIONS.settings.USER_MANAGEMENT_RESET_PASSWORD,
    ACTIONS.settings.CREATE_EDIT_MEDICAL_TEMPLATE,
    ACTIONS.settings.CLINIC_ROOM_CREATE_EDIT,
    ACTIONS.settings.DISCHARGE_ADVICE_CREATE,
    ACTIONS.settings.DISCHARGE_ADVICE_EDIT,
    ACTIONS.settings.DISCHARGE_ADVICE_DELETE,
    ACTIONS.services.PACKAGE_CREATE,
    ACTIONS.services.PACKAGE_EDIT,
    ACTIONS.inventory.VIEW_ADJUSTMENT,
    ACTIONS.inventory.CREATE_INVENTORY,
    ACTIONS.inventory.EDIT_INVENTORY,
    ACTIONS.inventory.DELETE_INVENTORY,
    ACTIONS.inventory.CREATE_SUPPLIER,
    ACTIONS.inventory.EDIT_SUPPLIER,
    ACTIONS.inventory.DEACTIVATE_SUPPLIER,
    ACTIONS.inventory.REACTIVATE_SUPPLIER,
    ACTIONS.inventory.CREATE_DELIVERY_ORDER,
    ACTIONS.inventory.EDIT_DELIVERY_ORDER,
    ACTIONS.inventory.DELETE_DELIVERY_ORDER,
    ACTIONS.inventory.FINALIZE_EDIT_DELIVERY_ORDER,
    ACTIONS.inventory.CREATE_DISPENSING_ORDER,
    ACTIONS.inventory.EDIT_DISPENSING_ORDER,
    ACTIONS.inventory.DELETE_DISPENSING_ORDER,
    ACTIONS.inventory.FINALIZE_EDIT_DISPENSING_ORDER,
    ACTIONS.invoice.CREATE_CREDIT_NOTE,
    ACTIONS.invoice.CLOSE_INVOICE,
    ACTIONS.invoice.DELETE_DISPENSE_ITEM,
    ACTIONS.invoice.NOTES_VIEW,
    ACTIONS.invoice.NOTES_EDIT,
    ACTIONS.invoice.PRINT_INVOICE,
    ACTIONS.invoice.UNIT_PRICE_EDIT,
    ACTIONS.invoice.ITEM_DISCOUNT_EDIT,
    ACTIONS.invoice.PROCESS_REFUND,
    ACTIONS.invoice.PRINT_CREDIT_NOTE,
    ACTIONS.invoice.PAYMENT_INVOICE,
    ACTIONS.invoice.VIEW_INVOICE_LIST,
    ACTIONS.invoice.VIEW_CREDIT_NOTE_LIST,
    ACTIONS.invoice.EDIT_PAYMENT,
    ACTIONS.queue.PRINT_PATIENT_LABEL,
    ACTIONS.queue.PRINT_MAILING_LABEL,
    ACTIONS.queue.PRINT_MEDICATION_LABEL,
    ACTIONS.queue.CANCEL_VISIT,
    ACTIONS.queue.EDIT_NOTES,
    ACTIONS.queue.UPDATE_PROVIDER,
    ACTIONS.queue.UPDATE_VISIT_TYPE,
    ACTIONS.queue.CALL_QUEUE,
    ACTIONS.appointment.VIEW_ALL_APPOINTMENTS,
    ACTIONS.appointment.EDIT_ALL_APPOINTMENTS,
    ACTIONS.appointment.VIEW_ALL_BLOCKED_SLOTS,
    ACTIONS.appointment.EDIT_ALL_BLOCKED_SLOTS,
    ACTIONS.appointment.PROVIDER_VIEW,
    ACTIONS.appointment.MONTH_VIEW,
    ACTIONS.appointment.WEEK_VIEW,
    ACTIONS.appointment.DAY_VIEW,
    ACTIONS.appointment.ROOM_VIEW,
    ACTIONS.queue.LINK_APPOINTMENT,
    ACTIONS.invoice.VOID_PAYMENT,
  ],
  [PERMISSION_ROLES.OpsSupport]: [
    ACTIONS.patient.ADD_TO_QUEUE,
    ACTIONS.patient.UPDATE_COVERAGE_PAGE,
    ACTIONS.patient.VIEW_DOCUMENT_TAB,
    ACTIONS.patient.MERGE_PATIENTS,
    ACTIONS.services.MEDICAL_SERVICES_CREATE,
    ACTIONS.services.MEDICAL_SERVICES_EDIT,
    ACTIONS.settings.USER_MANAGEMENT_RESET_PASSWORD,
    ACTIONS.settings.CREATE_EDIT_MEDICAL_TEMPLATE,
    ACTIONS.services.PACKAGE_CREATE,
    ACTIONS.services.PACKAGE_EDIT,
    ACTIONS.queue.PRINT_PATIENT_LABEL,
    ACTIONS.queue.PRINT_MAILING_LABEL,
    ACTIONS.queue.PRINT_MEDICATION_LABEL,
    ACTIONS.queue.CANCEL_VISIT,
    ACTIONS.queue.EDIT_NOTES,
    ACTIONS.queue.UPDATE_PROVIDER,
    ACTIONS.queue.UPDATE_VISIT_TYPE,
    ACTIONS.queue.CALL_QUEUE,
    ACTIONS.invoice.NOTES_VIEW,
    ACTIONS.invoice.VIEW_INVOICE_LIST,
    ACTIONS.invoice.VIEW_CREDIT_NOTE_LIST,
    ACTIONS.invoice.PRINT_INVOICE,
    ACTIONS.invoice.VOID_PAYMENT,
  ],
  [PERMISSION_ROLES.Finance]: [
    ACTIONS.patient.UPDATE_COVERAGE_PAGE,
    ACTIONS.services.MEDICAL_SERVICES_CREATE,
    ACTIONS.services.MEDICAL_SERVICES_EDIT,
    ACTIONS.services.PACKAGE_CREATE,
    ACTIONS.services.PACKAGE_EDIT,
    ACTIONS.inventory.CREATE_ADJUSTMENT,
    ACTIONS.inventory.VIEW_ADJUSTMENT,
    ACTIONS.inventory.CREATE_INVENTORY,
    ACTIONS.inventory.EDIT_INVENTORY,
    ACTIONS.inventory.DELETE_INVENTORY,
    ACTIONS.inventory.CREATE_SUPPLIER,
    ACTIONS.inventory.EDIT_SUPPLIER,
    ACTIONS.inventory.DEACTIVATE_SUPPLIER,
    ACTIONS.inventory.REACTIVATE_SUPPLIER,
    ACTIONS.inventory.CREATE_DELIVERY_ORDER,
    ACTIONS.inventory.EDIT_DELIVERY_ORDER,
    ACTIONS.inventory.DELETE_DELIVERY_ORDER,
    ACTIONS.inventory.FINALIZE_EDIT_DELIVERY_ORDER,
    ACTIONS.inventory.CREATE_DISPENSING_ORDER,
    ACTIONS.inventory.EDIT_DISPENSING_ORDER,
    ACTIONS.inventory.DELETE_DISPENSING_ORDER,
    ACTIONS.inventory.FINALIZE_EDIT_DISPENSING_ORDER,
    ACTIONS.invoice.CREATE_CREDIT_NOTE,
    ACTIONS.invoice.CLOSE_INVOICE,
    ACTIONS.invoice.NOTES_VIEW,
    ACTIONS.invoice.NOTES_EDIT,
    ACTIONS.invoice.PRINT_INVOICE,
    ACTIONS.invoice.ITEM_DISCOUNT_EDIT,
    ACTIONS.invoice.PRINT_CREDIT_NOTE,
    ACTIONS.invoice.PAYMENT_INVOICE,
    ACTIONS.invoice.VIEW_INVOICE_LIST,
    ACTIONS.invoice.VIEW_CREDIT_NOTE_LIST,
    ACTIONS.medicalCoverage.GENERATE_CORPORATE_INVOICE,
    ACTIONS.medicalCoverage.RECORD_GENERATE_UPLOAD_CPF_FILE,
    ACTIONS.medicalCoverage.ADD_DEPOSIT,
    ACTIONS.medicalCoverage.PRINT_CORPORATE_INVOICE,
    ACTIONS.medicalCoverage.VIEW_LIST,
    ACTIONS.queue.PRINT_PATIENT_LABEL,
    ACTIONS.queue.PRINT_MAILING_LABEL,
    ACTIONS.queue.PRINT_MEDICATION_LABEL,
    ACTIONS.queue.CANCEL_VISIT,
    ACTIONS.queue.UPDATE_PROVIDER,
    ACTIONS.queue.UPDATE_VISIT_TYPE,
    ACTIONS.chargeCodeV2.SET_ACTIVE,
    ACTIONS.chargeCodeV2.READ_WRITE,
  ],
  [PERMISSION_ROLES.BO]: [
    ACTIONS.patient.EDIT,
    ACTIONS.patient.EDIT_ALLERGIES,
    ACTIONS.patient.UPDATE_COVERAGE_PAGE,
    ACTIONS.invoice.CREATE_CREDIT_NOTE,
    ACTIONS.invoice.PROCESS_REFUND,
    ACTIONS.invoice.PRINT_CREDIT_NOTE,
    ACTIONS.invoice.PRINT_INVOICE,
    ACTIONS.invoice.PAYMENT_INVOICE,
    ACTIONS.invoice.NOTES_VIEW,
    ACTIONS.invoice.VIEW_INVOICE_LIST,
    ACTIONS.invoice.VIEW_CREDIT_NOTE_LIST,
    ACTIONS.invoice.EDIT_PAYMENT,
    ACTIONS.medicalCoverage.VIEW_LIST,
    ACTIONS.medicalCoverage.GENERATE_CORPORATE_INVOICE,
    ACTIONS.medicalCoverage.RECORD_GENERATE_UPLOAD_CPF_FILE,
    ACTIONS.medicalCoverage.ADD_DEPOSIT,
    ACTIONS.medicalCoverage.PRINT_CORPORATE_INVOICE,
  ],
  [PERMISSION_ROLES.LabUser]: [ACTIONS.invoice.NOTES_VIEW],
  [PERMISSION_ROLES.HelpDesk]: [ACTIONS.invoice.NOTES_VIEW],
  [PERMISSION_ROLES.InfectionControlManager]: [
    ACTIONS.patient.VIEW_INVESTIGATION_TAB,
    ACTIONS.invoice.NOTES_VIEW,
  ],
  [PERMISSION_ROLES.BD]: [
    ACTIONS.invoice.ITEM_DISCOUNT_EDIT,
    ACTIONS.invoice.PROCESS_REFUND,
    ACTIONS.invoice.PRINT_CREDIT_NOTE,
    ACTIONS.invoice.PAYMENT_INVOICE,
    ACTIONS.invoice.NOTES_VIEW,
    ACTIONS.invoice.VIEW_INVOICE_LIST,
    ACTIONS.invoice.VIEW_CREDIT_NOTE_LIST,
    ACTIONS.patient.CREATE,
  ],
  [PERMISSION_ROLES.ReadOnly]: [ACTIONS.invoice.NOTES_VIEW],
  // Charge Code Manager and Finance are the only roles allowed to change a charge code's active status
  [PERMISSION_ROLES.ChargeCodeManager]: [
    ACTIONS.chargeCodeV2.SET_ACTIVE,
    ACTIONS.chargeCodeV2.READ_WRITE,
  ],
};

/**
 * v2 permission name -> the legacy key the v1 tables are keyed by.
 *
 * Two disjoint key spaces exist in v1 and each keeps its own map, because a handful of v2
 * names cover both a sidebar page key and an `ACTIONS` entry whose role lists differ
 * (`User.Create` and `User.Update`). Resolving each call site in its own space is what makes
 * v1 behaviour byte-for-byte identical to before the sweep:
 *
 * - page space: the keys of `PREMISSIONS_BY_ROLE` (sidebar/page keys plus the
 *   `Consultation.*` / `Dispensing.*` / `Dashboard.*` constants)
 * - action space: the values of `ACTIONS`, looked up in `PREMISSIONS_ACTIONS_BY_ROLE`
 *
 * Both maps are built once at module load by walking the generated catalogue, so adding a
 * permission to `catalogue.v2.ts` needs no change here.
 */
export const V2_TO_LEGACY_PAGE_KEY: Record<string, string> = Object.fromEntries(
  Object.entries(PAGE_PERMISSION).map(([pageKey, v2Name]) => [v2Name, pageKey]),
);

const buildActionKeyMap = (): Record<string, string> => {
  const map: Record<string, string> = {};
  for (const [namespace, legacyEntries] of Object.entries(ACTIONS)) {
    const v2Entries = (PERMISSION as Record<string, Record<string, string> | undefined>)[namespace];
    if (!v2Entries) continue;
    for (const [key, legacyValue] of Object.entries(legacyEntries)) {
      const v2Name = v2Entries[key];
      if (v2Name) map[v2Name] = legacyValue;
    }
  }
  return map;
};

export const V2_TO_LEGACY_ACTION_KEY: Record<string, string> = buildActionKeyMap();

const LEGACY_ACTION_KEY_TO_V2: Record<string, string> = Object.fromEntries(
  Object.entries(V2_TO_LEGACY_ACTION_KEY).map(([v2Name, legacyKey]) => [legacyKey, v2Name]),
);

/**
 * Resolve a permission to the key the v1 `PREMISSIONS_BY_ROLE` table uses.
 * A legacy key passed in is returned unchanged, so mixed call sites keep working.
 */
export const toLegacyPageKey = (permission: string): string =>
  V2_TO_LEGACY_PAGE_KEY[permission] ?? permission;

/**
 * Resolve a permission to the key `useActionPermission` looked it up by before the sweep.
 * The action space wins over the page space so the shared `User.*` names keep their v1
 * action-table semantics at action call sites.
 */
export const toLegacyActionKey = (permission: string): string =>
  V2_TO_LEGACY_ACTION_KEY[permission] ?? V2_TO_LEGACY_PAGE_KEY[permission] ?? permission;

/**
 * Resolve a permission to its v2 name. Already-v2 names are returned unchanged.
 */
export const toV2PermissionName = (permission: string): string =>
  LEGACY_ACTION_KEY_TO_V2[permission] ?? PAGE_PERMISSION[permission] ?? permission;
